/**
 * ApplicationsTab.js
 * 
 * Displays user-defined cluster applications as clickable cards.
 * Shows applications that were configured in the cluster form.
 * Supports two launch types:
 * - 'url': Opens a web URL directly (if no instructions) or shows instructions first with a Launch button
 * - 'instructions': Shows a dialog with instructions and an action button
 * 
 * URL-type apps with instructions show a "View Instructions" chip and info button,
 * and clicking the app opens the instructions dialog with a "Launch Application" button.
 * 
 * Feature: cluster-apps
 */
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { 
	Paper, 
	Typography, 
	Icon, 
	Button, 
	Tooltip, 
	Chip, 
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	makeStyles 
} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: theme.spacing(3),
	},
	headerIcon: {
		fontSize: 28,
		marginRight: theme.spacing(1),
		color: theme.palette.primary.main,
	},
	headerTitle: {
		fontSize: 20,
		fontWeight: 600,
	},
	appsContainer: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
		gap: theme.spacing(2),
	},
	appCard: {
		display: 'flex',
		alignItems: 'center',
		padding: theme.spacing(2),
		borderRadius: 12,
		border: '2px solid #e5e7eb',
		backgroundColor: '#ffffff',
		transition: 'all 0.2s ease',
		'&:hover': {
			borderColor: theme.palette.primary.main,
			boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
		},
	},
	appCardDisabled: {
		display: 'flex',
		alignItems: 'center',
		padding: theme.spacing(2),
		borderRadius: 12,
		border: '2px solid #e5e7eb',
		backgroundColor: '#f9f9f9',
		opacity: 0.7,
	},
	iconButton: {
		minWidth: 'auto',
		width: 56,
		height: 56,
		padding: 0,
		borderRadius: 12,
		backgroundColor: '#f5f7fa',
		border: '2px solid #e5e7eb',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: 'border-color 0.2s',
		'&:hover': {
			borderColor: theme.palette.primary.main,
			backgroundColor: '#f0f4f8',
		},
	},
	iconButtonDisabled: {
		minWidth: 'auto',
		width: 56,
		height: 56,
		padding: 0,
		borderRadius: 12,
		backgroundColor: '#f0f0f0',
		border: '2px solid #e0e0e0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'not-allowed',
	},
	appIcon: {
		fontSize: 28,
		color: theme.palette.primary.main,
	},
	appIconDisabled: {
		fontSize: 28,
		color: theme.palette.text.disabled,
	},
	appInfo: {
		marginLeft: theme.spacing(2),
		flex: 1,
		minWidth: 0,
	},
	appName: {
		fontWeight: 600,
		fontSize: 16,
		color: theme.palette.primary.main,
		cursor: 'pointer',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	appNameDisabled: {
		fontWeight: 600,
		fontSize: 16,
		color: theme.palette.text.disabled,
		cursor: 'not-allowed',
	},
	appDescription: {
		color: theme.palette.text.secondary,
		fontSize: 13,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
	},
	appUrl: {
		color: theme.palette.text.hint,
		fontSize: 12,
		marginTop: 4,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	pendingChip: {
		marginTop: 4,
		height: 20,
		fontSize: 11,
	},
	instructionsChip: {
		marginTop: 4,
		height: 20,
		fontSize: 11,
	},
	infoButton: {
		minWidth: 'auto',
		width: 32,
		height: 32,
		padding: 0,
		borderRadius: 8,
		marginLeft: 'auto',
		flexShrink: 0,
	},
	emptyState: {
		textAlign: 'center',
		padding: theme.spacing(6),
		color: theme.palette.text.secondary,
	},
	emptyIcon: {
		fontSize: 48,
		marginBottom: theme.spacing(2),
		color: theme.palette.text.disabled,
	},
}));

/**
 * Get the server IP from cluster's terraform info or commissioned machine
 * @param {Object} cluster - Cluster object with terraform info or network config
 * @returns {string} Server IP or empty string
 */
const getServerIp = (cluster) => {
	// Primary: Get IP from terraform info (set after Terraform deployment)
	if (cluster?.info?.terraform) {
		const terraform = cluster.info.terraform;
		if (terraform.publicIp || terraform.privateIp) {
			return terraform.publicIp || terraform.privateIp;
		}
	}
	
	// Secondary: Get IP from network config (for commissioned machines / MaaS clusters)
	// Commissioned machines have IP pre-configured before deployment
	if (cluster?.data?.network) {
		const network = cluster.data.network;
		if (network.public_ip || network.private_static_ip) {
			return network.public_ip || network.private_static_ip;
		}
	}
	
	// Fallback: Check nodes array (for legacy clusters)
	if (cluster?.nodes?.length > 0) {
		const headNode = cluster.nodes.find(n => n.type === 'head-node') || cluster.nodes[0];
		return headNode.publicIp || headNode.privateIp || '';
	}
	
	return '';
};

/**
 * Check if the app URL requires server IP and if it's available
 * @param {Object} app - Application object with url and optional port
 * @param {string} serverIp - The server IP
 * @returns {boolean} True if URL can be built
 */
const canBuildUrl = (app, serverIp) => {
	const url = app.url || '';
	
	// If URL contains placeholders, we need the server IP to replace them
	if (url.includes('<server-ip>') || url.includes('{server-ip}')) {
		return !!serverIp;
	}
	
	// If URL already has a full http/https URL with a hostname, it's valid
	if (url.startsWith('http://') || url.startsWith('https://')) {
		// Check if URL has a valid hostname (not just http:// or https://)
		try {
			const parsed = new URL(url);
			return !!parsed.hostname;
		} catch {
			return false;
		}
	}
	
	// URL is a path or port-based, requires server IP
	return !!serverIp;
};

/**
 * Build the full application URL using cluster's head node IP
 * @param {Object} app - Application object with url and optional port
 * @param {string} serverIp - The server IP
 * @returns {string|null} Full URL to the application or null if cannot build
 */
const buildAppUrl = (app, serverIp) => {
	if (!canBuildUrl(app, serverIp)) {
		return null;
	}
	
	let url = app.url || '';
	
	// If URL already has http/https, use as-is (with IP substitution)
	if (url.includes('<server-ip>') || url.includes('{server-ip}')) {
		url = url.replace(/<server-ip>/g, serverIp).replace(/{server-ip}/g, serverIp);
	} else if (!url.startsWith('http://') && !url.startsWith('https://')) {
		// Build URL from IP and path
		const path = url.startsWith('/') ? url : (url ? `/${url}` : '');
		url = `http://${serverIp}${path}`;
	}
	
	return url;
};

/**
 * Get display URL for the app (what to show to the user)
 * @param {Object} app - Application object
 * @param {string} serverIp - The server IP
 * @returns {string} Display URL
 */
const getDisplayUrl = (app, serverIp) => {
	const url = buildAppUrl(app, serverIp);
	if (url) {
		return url;
	}
	
	// Show what the URL will look like when IP is available
	const path = app.url || '';
	if (path.startsWith('http://') || path.startsWith('https://')) {
		return path; // Show as-is with placeholders
	}
	return `http://<pending-ip>${path.startsWith('/') ? path : (path ? `/${path}` : '')}`;
};

const ApplicationsTab = ({ cluster, applications = [], onNavigateToAccess, onLaunchVNC }) => {
	const classes = useStyles();
	const history = useHistory();
	
	// State for instructions dialog
	const [instructionsDialog, setInstructionsDialog] = useState({
		open: false,
		app: null
	});
	
	// Get applications from cluster object if not passed directly
	const clusterApps = applications.length > 0 ? applications : (cluster?.applications || []);
	
	// Get server IP once for all apps
	const serverIp = getServerIp(cluster);
	
	// Handle clicking an app - either open URL or show instructions dialog
	const handleAppClick = (app) => {
		if (app.launchType === 'instructions') {
			// Show instructions dialog
			setInstructionsDialog({ open: true, app });
		} else if (app.instructions && app.instructions.trim()) {
			// URL-type app with instructions — show instructions first with launch button
			setInstructionsDialog({ open: true, app });
		} else {
			// Default: open URL directly (no instructions to show)
			handleLaunchUrl(app);
		}
	};
	
	// View instructions without launching (for the info button)
	const handleViewInstructions = (app) => {
		setInstructionsDialog({ open: true, app });
	};
	
	// Launch URL in new tab
	const handleLaunchUrl = (app) => {
		const url = buildAppUrl(app, serverIp);
		if (url) {
			window.open(url, '_blank', 'noopener,noreferrer');
		}
	};
	
	// Close instructions dialog
	const handleCloseInstructions = () => {
		setInstructionsDialog({ open: false, app: null });
	};
	
	// Handle action button in instructions dialog
	const handleInstructionsAction = (app) => {
		handleCloseInstructions();
		
		if (app.launchType === 'url' || (!app.launchType && app.url)) {
			// URL-type app — launch the URL
			handleLaunchUrl(app);
		} else if (app.actionType === 'access-tab') {
			// Navigate to Access tab
			if (onNavigateToAccess) {
				onNavigateToAccess();
			} else {
				// Fallback: update URL hash
				const clusterId = cluster?.id;
				if (clusterId) {
					history.push(`/clusters/${clusterId}#access`);
				} else {
					window.location.hash = 'access';
				}
			}
		} else if (app.actionType === 'remote-desktop') {
			// Launch VNC directly
			if (onLaunchVNC) {
				onLaunchVNC();
			} else {
				// Fallback: navigate to access tab with VNC auto-launch param
				const clusterId = cluster?.id;
				if (clusterId) {
					history.push(`/clusters/${clusterId}?autoLaunchVNC=true#access`);
				}
			}
		}
		// 'none' action type just closes the dialog (already done above)
	};
	
	// Get action button label
	const getActionButtonLabel = (app) => {
		if (app.actionButtonLabel) {
			return app.actionButtonLabel;
		}
		// URL-type apps get a "Launch" button
		if (app.launchType === 'url' || (!app.launchType && app.url)) {
			return 'Launch Application';
		}
		switch (app.actionType) {
			case 'remote-desktop':
				return 'Open Remote Desktop';
			case 'access-tab':
				return 'Go to Access Tab';
			case 'none':
			default:
				return 'Close';
		}
	};
	
	// Get action button icon
	const getActionButtonIcon = (app) => {
		// URL-type apps get a launch icon
		if (app.launchType === 'url' || (!app.launchType && app.url)) {
			return 'open_in_new';
		}
		switch (app.actionType) {
			case 'remote-desktop':
				return 'desktop_windows';
			case 'access-tab':
				return 'vpn_key';
			case 'none':
			default:
				return 'close';
		}
	};
	
	if (!clusterApps || clusterApps.length === 0) {
		return (
			<div className={classes.root}>
				<Paper className={classes.emptyState} elevation={1}>
					<Icon className={classes.emptyIcon}>apps</Icon>
					<Typography variant="h6">No Applications Configured</Typography>
					<Typography variant="body2">
						Applications can be configured in the cluster form under Advanced settings.
					</Typography>
				</Paper>
			</div>
		);
	}
	
	return (
		<div className={classes.root}>
			<Paper className="p-24 rounded-lg shadow-md">
				<div className={classes.header}>
					<Icon className={classes.headerIcon}>apps</Icon>
					<Typography className={classes.headerTitle}>
						Cluster Applications
					</Typography>
				</div>
				
				<div className={classes.appsContainer}>
					{clusterApps.map((app, idx) => {
						const isInstructionsType = app.launchType === 'instructions';
						const isAvailable = isInstructionsType || canBuildUrl(app, serverIp);
						const appUrl = isInstructionsType ? null : buildAppUrl(app, serverIp);
						const iconName = app.icon || 'apps';
						
						// Tooltip text based on app type
						let tooltipText = 'Waiting for cluster IP...';
						if (isAvailable) {
							tooltipText = isInstructionsType 
								? `View ${app.name} instructions` 
								: `Launch ${app.name}`;
						}
						
						return (
							<div 
								key={app.name || idx} 
								className={isAvailable ? classes.appCard : classes.appCardDisabled}
							>
								<Tooltip 
									title={tooltipText} 
									arrow
								>
									<span> {/* Wrapper needed for disabled button tooltip */}
										<Button
											className={isAvailable ? classes.iconButton : classes.iconButtonDisabled}
											onClick={() => isAvailable && handleAppClick(app)}
											disabled={!isAvailable}
										>
											<Icon className={isAvailable ? classes.appIcon : classes.appIconDisabled}>
												{iconName}
											</Icon>
										</Button>
									</span>
								</Tooltip>
								
								<div className={classes.appInfo}>
									<Typography 
										className={isAvailable ? classes.appName : classes.appNameDisabled}
										onClick={() => isAvailable && handleAppClick(app)}
									>
										{app.name}
									</Typography>
									{app.description && (
										<Typography className={classes.appDescription}>
											{app.description}
										</Typography>
									)}
									{isInstructionsType && (
										<Chip
											size="small"
											icon={<Icon style={{ fontSize: 14 }}>description</Icon>}
											label="Instructions"
											className={classes.instructionsChip}
											color="primary"
											onClick={() => handleAppClick(app)}
										/>
									)}
									{!isInstructionsType && app.instructions && app.instructions.trim() && (
										<Chip
											size="small"
											icon={<Icon style={{ fontSize: 14 }}>info_outline</Icon>}
											label="View Instructions"
											className={classes.instructionsChip}
											color="primary"
											variant="outlined"
											onClick={() => handleViewInstructions(app)}
										/>
									)}
									{!isInstructionsType && isAvailable && appUrl && (
										<Tooltip title={appUrl} arrow>
											<Typography className={classes.appUrl}>
												{appUrl}
											</Typography>
										</Tooltip>
									)}
									{!isInstructionsType && !isAvailable && (
										<Chip
											size="small"
											icon={<Icon style={{ fontSize: 14 }}>schedule</Icon>}
											label="Waiting for cluster IP"
											className={classes.pendingChip}
											color="default"
										/>
									)}
								</div>
								{/* Info button for URL-type apps with instructions */}
								{!isInstructionsType && isAvailable && app.instructions && app.instructions.trim() && (
									<Tooltip title="View instructions before launch" arrow>
										<Button
											className={classes.infoButton}
											onClick={(e) => {
												e.stopPropagation();
												handleViewInstructions(app);
											}}
											size="small"
										>
											<Icon style={{ fontSize: 20, color: '#1976d2' }}>info_outline</Icon>
										</Button>
									</Tooltip>
								)}
							</div>
						);
					})}
				</div>
			</Paper>
			
			{/* Instructions Dialog */}
			<Dialog
				open={instructionsDialog.open}
				onClose={handleCloseInstructions}
				maxWidth="md"
				fullWidth
				classes={{ paper: classes.instructionsDialog }}
			>
				{instructionsDialog.app && (
					<>
						<DialogTitle>
							<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
								<Icon color="primary">{instructionsDialog.app.icon || 'apps'}</Icon>
								<span>{instructionsDialog.app.name}</span>
							</div>
						</DialogTitle>
						<DialogContent dividers>
							{instructionsDialog.app.description && (
								<Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
									{instructionsDialog.app.description}
								</Typography>
							)}
							<div 
								dangerouslySetInnerHTML={{ 
									__html: instructionsDialog.app.instructions || 'No instructions provided.' 
								}}
								style={{
									lineHeight: 1.7,
									'& pre': {
										backgroundColor: '#f5f5f5',
										padding: 12,
										borderRadius: 4,
										overflow: 'auto'
									},
									'& code': {
										backgroundColor: '#f0f0f0',
										padding: '2px 6px',
										borderRadius: 3,
										fontFamily: 'monospace'
									}
								}}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCloseInstructions} color="default">
								Close
							</Button>
							{/* Show launch button for URL-type apps */}
							{(instructionsDialog.app.launchType === 'url' || (!instructionsDialog.app.launchType && instructionsDialog.app.url)) && (
								<Button 
									onClick={() => handleInstructionsAction(instructionsDialog.app)}
									color="primary"
									variant="contained"
									startIcon={<Icon>open_in_new</Icon>}
								>
									{getActionButtonLabel(instructionsDialog.app)}
								</Button>
							)}
							{/* Show action button for instructions-type apps */}
							{instructionsDialog.app.launchType === 'instructions' && instructionsDialog.app.actionType && instructionsDialog.app.actionType !== 'none' && (
								<Button 
									onClick={() => handleInstructionsAction(instructionsDialog.app)}
									color="primary"
									variant="contained"
									startIcon={<Icon>{getActionButtonIcon(instructionsDialog.app)}</Icon>}
								>
									{getActionButtonLabel(instructionsDialog.app)}
								</Button>
							)}
						</DialogActions>
					</>
				)}
			</Dialog>
		</div>
	);
};

export default ApplicationsTab;
